import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { useTablesContext } from "../../../context/tablesContextProvider";
import { AestraPrimaryCareTable } from "../../../data-model/aestraPrimaryCareTable";
import { TableNames } from "../../../enums/tableNames";
import { useAbortableFetch } from "../../../hooks/useAbortableFetch";
import { RootState } from "../../../redux/store/store";
import { makePostCallForInitialDataFetch } from "../apis/makePostCallForInitialDataFetch";
import { processFilterData } from "../utils/processFilterData";
import { PrimaryCareFilterPayload } from "./useFilterPayload";
import { PrimaryCareTableState } from "./usePrimaryCareTableData";

export interface UseInitialDataFetchingForTableReturn {
  initialDataFetchingExecuted: boolean;
  setInitialDataFetchingExecuted: React.Dispatch<React.SetStateAction<boolean>>;
}

export const useInitialDataFetchingForTable = (
  setPrimaryCareState: React.Dispatch<
    React.SetStateAction<PrimaryCareTableState>
  >,
  setTableData: React.Dispatch<React.SetStateAction<AestraPrimaryCareTable[]>>,
  dateFromStart: string,
  dateFromEnd: string,
  pageSize: number,
): UseInitialDataFetchingForTableReturn => {
  const { setTableDataForContext, getTableDataFromContext } =
    useTablesContext();
  const [initialDataFetchingExecuted, setInitialDataFetchingExecuted] =
    useState<boolean>(false);

  const tableAttributes = getTableDataFromContext(
    TableNames.PHARMA_PRIMARY_CARE_ORDERS,
  );

  const { filters } = useSelector((state: RootState) => state.filterValues);

  const validFilters = useMemo(() => {
    return filters.filter(
      (filter) => filter.name === TableNames.PHARMA_PRIMARY_CARE_ORDERS,
    );
  }, [filters]);

  const { runFetch: runInitialDataFetch } = useAbortableFetch(
    makePostCallForInitialDataFetch,
  );

  const setPrimaryCareStateRef = useRef(setPrimaryCareState);
  const setTableDataRef = useRef(setTableData);
  const setTableDataForContextRef = useRef(setTableDataForContext);

  useEffect(() => {
    setPrimaryCareStateRef.current = setPrimaryCareState;
    setTableDataRef.current = setTableData;
    setTableDataForContextRef.current = setTableDataForContext;
  }, [setPrimaryCareState, setTableData, setTableDataForContext]);

  useEffect(() => {
    if (!initialDataFetchingExecuted && dateFromStart && dateFromEnd) {
      const searchedText = tableAttributes?.searchedText ?? "";

      const apiFilters = processFilterData(validFilters);

      const payload: PrimaryCareFilterPayload = {
        filters: apiFilters,
        page: 1,
        page_size: pageSize,
        request_id: uuidv4(),
        cmm_fields: ["cmm_status", "error_text", "cmm_result_key"],
      };

      // Add keyword if search text exists
      if (searchedText && searchedText.trim().length > 0) {
        payload.keyword = searchedText.trim();
      }

      runInitialDataFetch(
        payload,
        setPrimaryCareStateRef.current,
        setTableDataRef.current,
      ).then(() => {
        setInitialDataFetchingExecuted(true);

        setTableDataForContextRef.current(
          TableNames.PHARMA_PRIMARY_CARE_ORDERS,
          {
            lastBatchFetched: 1,
          },
        );
      });
    }
  }, [
    initialDataFetchingExecuted,
    dateFromStart,
    dateFromEnd,
    pageSize,
    tableAttributes?.searchedText,
    validFilters,
    runInitialDataFetch,
  ]);

  return {
    initialDataFetchingExecuted,
    setInitialDataFetchingExecuted,
  };
};
